/**
 * Render-side resolution of an AnimationTree's `tree_root` into a typed `AnimNode` graph for
 * `evaluateTree`. THREE-free and React-free: it reads only the SubResource `data` strings, the
 * `internalResources` that `resolveAnimations` also reads. A `tree_root` is a BlendTree (sub-nodes
 * wired by `node_connections`) or a StateMachine, whose leaves are `AnimationNodeAnimation` clips.
 */
import { unquoteLiteral } from '../../../parser/utils';
import { findSubResource } from '../../../resources/SubResourceResolver';
import { ARRAY_LITERAL_RE, arrayLiteralBody } from '../../../godot/variant/variantParser.js';
import { boolSlotValue } from '../../../godot/variant/variantBool.js';
import { dropTrailingComma, splitTopLevel } from '../../../godot/string/string.js';
import { slotInt } from '../../../godot/variant/intSlots.js';
import { subResourceRefAnywhere } from '../../../godot/variant/resourceRef.js';
export function resolveTreeRoot(treeRootRef, resources) {
    if (!treeRootRef)
        return null;
    const id = extractSubResourceId(treeRootRef);
    if (id === null)
        return null;
    return resolveNodeById(id, resources, new Set());
}
/** Extract the id from a `SubResource("id")` reference. */
function extractSubResourceId(ref) {
    return subResourceRefAnywhere(ref);
}
/**
 * The one door from a sub-resource id to a node, so the cycle guard covers every recursion path.
 * Godot allows a cycle (animation_blend_tree.cpp:1489-1493 and :1615-1619 refuse neither), and a
 * re-entry in this render-phase `useMemo` would blank the preview. `visiting` is the per-branch
 * path, not a visited set, so one sub-resource wired into two ports resolves on both.
 */
function resolveNodeById(id, resources, visiting, name = '', inputs = () => null) {
    if (visiting.has(id))
        return null;
    const resource = findSubResource(resources, id);
    if (!resource)
        return null;
    return resolveInnerNode(resource, name, resources, new Set(visiting).add(id), inputs);
}
/**
 * Resolve a non-BlendTree AnimationNode whose inputs (if any) come from a
 * surrounding BlendTree's connections. `name` is the node's BlendTree-local
 * name (empty when the node is the `tree_root` itself, with no inputs). The
 * `inputs` map yields an input port's resolved node.
 */
function resolveInnerNode(resource, name, resources, visiting, inputs = () => null) {
    switch (resource.type) {
        case 'AnimationNodeAnimation': {
            const clip = unquoteLiteral((resource.data['animation'] ?? '').trim());
            return clip.length > 0 ? { kind: 'animation', clip } : null;
        }
        case 'AnimationNodeBlend2':
            return {
                kind: 'blend2',
                name,
                filtered: boolSlotValue(resource.data['filter_enabled']) === true,
                in0: inputs(0),
                in1: inputs(1),
            };
        case 'AnimationNodeAdd2':
            return { kind: 'add2', name, base: inputs(0), add: inputs(1) };
        case 'AnimationNodeTimeScale':
            return { kind: 'timescale', name, input: inputs(0) };
        case 'AnimationNodeBlendTree':
            return resolveBlendTree(resource, resources, visiting);
        case 'AnimationNodeStateMachine':
            return resolveStateMachine(resource, name, resources, visiting);
        default:
            return { kind: 'passthrough', type: resource.type, name, input: inputs(0) };
    }
}
/**
 * Resolve an `AnimationNodeBlendTree`: read its `nodes/<name>/node`
 * sub-resource map and `node_connections` wiring, then resolve the node feeding
 * the implicit `output` sink (port 0).
 */
function resolveBlendTree(blendTree, resources, visiting) {
    const nodeIds = parseBlendTreeNodes(blendTree.data);
    const connections = parseConnections(blendTree.data['node_connections'] ?? '');
    const resolveLocal = (localName, seen) => {
        if (seen.has(localName))
            return null; // cyclic connection guard
        const subId = nodeIds.get(localName);
        if (subId === undefined)
            return null;
        const nextSeen = new Set(seen).add(localName);
        return resolveNodeById(subId, resources, visiting, localName, (port) => {
            const from = connections.get(`${localName}:${port}`);
            return from === undefined ? null : resolveLocal(from, nextSeen);
        });
    };
    const rootName = connections.get('output:0');
    return rootName === undefined ? null : resolveLocal(rootName, new Set());
}
/**
 * `nodes/<name>/node = SubResource("id")` → `name → id`. A quoted key reads as the tokenizer reads
 * it, escapes decoded, so a name holding `"` matches the `&"…"` token that wires it.
 */
function parseBlendTreeNodes(data) {
    const out = new Map();
    for (const [rawKey, rawValue] of Object.entries(data)) {
        const key = unquoteLiteral(rawKey);
        const match = /^nodes\/(.+)\/node$/.exec(key);
        if (!match || match[1] === undefined)
            continue;
        const id = extractSubResourceId(rawValue);
        if (id !== null)
            out.set(match[1], id);
    }
    return out;
}
/**
 * Parse a BlendTree's `node_connections` flat array of
 * `[&"toNode", port, &"fromNode", …]` triples into a
 * `"toNode:port" → fromNode` map.
 */
function parseConnections(raw) {
    // Split the array rather than scanning tokens: a `&"…"|-?\d+` scan reads the port `1e1` as two
    // tokens, shifting every later triple, and skips a plain `"Blend"`, which
    // `Variant::operator StringName()` converts and Godot therefore wires.
    const body = ARRAY_LITERAL_RE.exec(raw.trim());
    if (!body)
        return new Map();
    const tokens = dropTrailingComma(splitTopLevel(body[1]));
    const out = new Map();
    for (let i = 0; i + 2 < tokens.length; i += 3) {
        const to = unquoteLiteral(tokens[i]);
        // `connect_node`'s port is an int slot (animation_blend_tree.cpp:1766), read like every int
        // slot, including a string, which `Variant::_to_int` routes through `String::to_int()`
        // (variant.h:372). An unreadable `"0"` would drop the triple and leave the tree with no root.
        const port = slotInt(unquoteLiteral(tokens[i + 1]));
        const from = unquoteLiteral(tokens[i + 2]);
        if (port === null)
            continue;
        out.set(`${to}:${port}`, from);
    }
    return out;
}
/**
 * Resolve an `AnimationNodeStateMachine`: read its `states/<name>/node`
 * sub-resources and pick a start state. With no runtime `travel`, the start
 * state is the target of a transition from the implicit `Start` node, falling
 * back to the first authored state.
 */
function resolveStateMachine(resource, name, resources, visiting) {
    const states = [];
    for (const [rawKey, rawValue] of Object.entries(resource.data)) {
        const match = /^states\/(.+)\/node$/.exec(unquoteLiteral(rawKey));
        if (!match || match[1] === undefined)
            continue;
        const subId = extractSubResourceId(rawValue);
        const node = subId === null ? null : resolveNodeById(subId, resources, visiting);
        states.push({ name: match[1], node });
    }
    return {
        kind: 'statemachine',
        name,
        states,
        startState: pickStartState(resource.data['transitions'] ?? '', states),
    };
}
/**
 * Target of a `Start →` transition, else the first authored state.
 *
 * `transitions = [&"Start", &"Idle", SubResource("…"), …]` is split as the
 * array it is, the way `parseConnections` reads its triples.
 */
function pickStartState(transitions, states) {
    const body = arrayLiteralBody(transitions);
    const tokens = body === null ? [] : dropTrailingComma(splitTopLevel(body));
    for (let i = 0; i + 2 < tokens.length; i += 3) {
        const from = unquoteLiteral(tokens[i]);
        if (from === 'Start')
            return unquoteLiteral(tokens[i + 1]);
    }
    return states[0]?.name ?? null;
}
//# sourceMappingURL=treeResources.js.map